import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AriaModal from 'react-aria-modal';
import { useT } from '~app/core/BundleProvider';
import Table from '~app/features/app/components/Table';
import { actions as CanvasActions } from '~app/features/canvas';
import { actions as SaveLoadActions } from '~app/features/saveLoad';
import { actions as DocumentActions } from '~app/features/document';
import ButtonBox from '~app/features/app/components/ButtonBox';
import withEnterToClick from '~app/core/withEnterToClick';
import IconButton from '~app/features/app/components/IconButton';
import ReferenceTableRow from './ReferenceTableRow';
import { actions as GradingActions } from '~app/features/gradingTool';
import { actions as ModelActions } from '~app/features/landing';
import Logging from '~app/core/logging';
import useGlobalHotkey from '~app/core/useGlobalHotkey';

const ClickOrEnterDiv = withEnterToClick('div');

const getKey = row => row.uuid;
const log = Logging.getLogger('app.features.saveLoad.index');

const Landing = () => {
  const t = useT();
  const dispatch = useDispatch();
  const history = useHistory();
  const [searchInput, setSearchInput] = useState('');
  const [fileData, setFileData] = useState(null);
  const [showCreationToolSelect, setShowCreationToolSelect] = useState(false);
  const hasDocument = useSelector(
    state => !!state.document.present?.workingModel,
  );

  const [showUpdateModelTypePopup, setShowUpdateModelTypePopup] = useState(
    false,
  );

  const modelType = useSelector(state => state.model?.type);
  const fileUploader = useRef();
  const inGradingMode = useSelector(state => state.grading?.inGradingMode);
  const referenceTablePath = './reference_models/landing.json';
  const [referenceModelList, setData] = useState([]);

  useEffect(() => {
    if (inGradingMode) {
      dispatch(GradingActions.storeSubmissions([]));
      dispatch(DocumentActions.closeModel());
      dispatch(GradingActions.setGradingResults([]));
      dispatch(GradingActions.setSubmissionUuids([]));
      dispatch(GradingActions.setGradingMode(false));
    } else if (hasDocument && modelType && modelType !== 'graph') {
      history.push('/canvas');
    }
  }, [dispatch, hasDocument, history, inGradingMode, modelType]);

  useEffect(() => {
    (async function () {
      try {
        const response = await fetch(referenceTablePath, {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });
        const responseJSON = await response.json();
        setData(responseJSON);
      } catch (e) {
        log.debug(e);
      }
    })();
  }, []);

  const onCancel = () => {
    setShowUpdateModelTypePopup(false);
    dispatch(ModelActions.setModelType({ type: '' })); // reset type
  };

  const changeModelType = useCallback(
    modelType => {
      dispatch(CanvasActions.setDefaultModelType(modelType));
      dispatch(ModelActions.setModelType({ type: modelType }));
      // re-parse the file again after changing the type
      dispatch(SaveLoadActions.loadFromLocal({ file: fileData }));
    },
    [dispatch, fileData],
  );
  useEffect(() => {
    if (modelType === 'graph') {
      setShowUpdateModelTypePopup(true);
    }
  }, [modelType]);

  useGlobalHotkey({
    hotkey: 'Escape',
    callback: onCancel,
    event: 'keyup',
    priority: 10,
    stop: true,
  });

  const handleFileUpload = useCallback(
    e => {
      dispatch(CanvasActions.setDefaultEditMode(''));
      dispatch(ModelActions.setModelType({ type: '' })); // reset type
      const file = e.target.files[0];
      setFileData(file);
      dispatch(SaveLoadActions.loadFromLocal({ file }));
    },
    [dispatch],
  );

  const handleCreationToolClick = useCallback(
    modelType => {
      dispatch(CanvasActions.setDefaultEditMode('solution'));
      dispatch(CanvasActions.setDefaultModelType(modelType));
      dispatch(CanvasActions.collapseLabelBank());
      const name = t('new-model');
      const instructions = t('add-instructions');
      dispatch(DocumentActions.createModel({ name, instructions }));
      history.push('/canvas');
    },
    [history, dispatch, t],
  );

  const handleGradingToolClick = useCallback(
    e => {
      // add set edit mode action
      history.push('/grading-tool');
    },
    [history],
  );

  const modelButtons = [
    {
      text: t('load'),
      onClick: () => fileUploader.current.click(),
    },
  ];

  const gradeToolButton = [
    {
      text: t('grade-models'),
      onClick: handleGradingToolClick,
    },
  ];

  const createToolButton = [
    {
      text: t('create-model'),
      onClick: () => setShowCreationToolSelect(true),
    },
  ];

  const filterData = ReferenceModel => {
    if (
      ReferenceModel.name?.toLowerCase().includes(searchInput) ||
      ReferenceModel.type?.toLowerCase().includes(searchInput) ||
      ReferenceModel.state?.toLowerCase().includes(searchInput) ||
      ReferenceModel.description?.toLowerCase().includes(searchInput)
    ) {
      return ReferenceModel;
    }
  };

  const tableColumns = [
    {
      name: 'thumb',
      sortable: false,
      className: 'Table__col-thumb',
    },
    {
      name: 'name',
      title: t('title'),
      alt: t('sort-by-title'),
      sortable: true,
      className: 'Table__col-title',
    },
    {
      name: 'type',
      title: t('type'),
      alt: t('sort-by-type'),
      sortable: true,
      className: 'Table__col-type',
    },
    {
      name: 'description',
      title: t('description'),
      sortable: false,
      className: 'Table__col-description',
    },
  ];

  return (
    <div role="main" className="Page__container Page_container-landing">
      <div className="ButtonBox__container">
        <ButtonBox
          buttons={modelButtons}
          title={t('open-a-model')}
          padded={true}
        />
        <ButtonBox buttons={createToolButton} title={t('creation-tool')} />
        <ButtonBox buttons={gradeToolButton} title={t('grading-tool')} />
      </div>
      <input
        type="file"
        id="file"
        value=""
        ref={fileUploader}
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        aria-label={t('file-upload')}
        aria-hidden
      />
      <AriaModal
        mounted={showCreationToolSelect}
        onExit={() => setShowCreationToolSelect(false)}
        titleText={t('creation-tool')}
      >
        <div className="Modal__container">
          <div className="ButtonBox__box ButtonBox__CreationTool">
            <div className="Modal__exit-container">
              <div className="Modal__icon">
                <IconButton
                  styleName="Modal__exit"
                  onClick={() => setShowCreationToolSelect(false)}
                  ariaLabel={t('create-close-menu')}
                  tabIndex="0"
                />
              </div>
            </div>
            <h2 className="mb-1">{t('creation-tool')}</h2>
            <div className={'ButtonBox__description mb-3'}>
              {t('select-model-type')}
            </div>
            <div className="row">
              <div className="col">
                <div className="CreationTool__inner-container">
                  <h3 className="ButtonBox__subtitle">
                    {t('conceptual-diagram')}
                  </h3>
                  <div className={'ButtonBox__description'}>
                    {t('conceptual-description')}
                  </div>
                  <div className="ButtonBox__button-container-fixed">
                    <ClickOrEnterDiv
                      className="ButtonBox__button"
                      onClick={() => handleCreationToolClick('conceptual')}
                      role="button"
                      aria-label="Create Conceptual Diagram"
                      tabIndex="0"
                    >
                      {t('create')}
                    </ClickOrEnterDiv>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="CreationTool__inner-container">
                  <h3 className="ButtonBox__subtitle">{t('causal-diagram')}</h3>
                  <div className={'ButtonBox__description'}>
                    {t('causal-description')}
                  </div>
                  <div className="ButtonBox__button-container-fixed">
                    <ClickOrEnterDiv
                      className="ButtonBox__button"
                      onClick={() => handleCreationToolClick('causal')}
                      role="button"
                      aria-label="Create Causal Diagram"
                      tabIndex="0"
                    >
                      {t('create')}
                    </ClickOrEnterDiv>
                  </div>
                </div>
              </div>
              <div className="col">
                <div className="CreationTool__inner-container">
                  <h3 className="ButtonBox__subtitle">
                    {t('stock-flow-diagram')}
                  </h3>
                  <div className={'ButtonBox__description'}>
                    {t('stock-flow-description')}
                  </div>
                  <div className="ButtonBox__button-container-fixed">
                    <ClickOrEnterDiv
                      className="ButtonBox__button"
                      onClick={() => handleCreationToolClick('stockflow')}
                      role="button"
                      aria-label="Create Stock and Flow Diagram"
                      tabIndex="0"
                    >
                      {t('create')}
                    </ClickOrEnterDiv>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AriaModal>
      <AriaModal
        mounted={showUpdateModelTypePopup}
        titleText={t('change-model-type-title')}
      >
        <div className="ConfirmationDialog Modal__container">
          <div className="ConfirmationDialog__text">
            {t('change-model-type-text')}
          </div>
          <div className="ConfirmationDialog__button-group">
            <ClickOrEnterDiv
              className="ConfirmationDialog__button"
              onClick={onCancel}
              role="button"
              tabIndex={0}
            >
              {t('cancel-button')}
            </ClickOrEnterDiv>
            <ClickOrEnterDiv
              className="ConfirmationDialog__button"
              onClick={() => changeModelType('conceptual')}
              role="button"
              tabIndex={0}
            >
              {t('conceptual-model')}
            </ClickOrEnterDiv>
            <ClickOrEnterDiv
              className="ConfirmationDialog__button"
              onClick={() => changeModelType('causal')}
              role="button"
              tabIndex={0}
            >
              {t('causal-model')}
            </ClickOrEnterDiv>
          </div>
        </div>
      </AriaModal>
      <div className="Table__container">
        <Table
          TableRow={ReferenceTableRow}
          tableColumns={tableColumns}
          data={referenceModelList}
          title={t('choose-model')}
          subtitle={t('choose-model-subtitle')}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          filterData={filterData}
          defaultSort="name"
          searchable
          getKey={getKey}
        />
      </div>
    </div>
  );
};

export default Landing;
